/* Services Base */
import { authService } from "./";

/* Constants */
const PREFIX = "ING:";

/* Variables */
const _top = window.opener
    ? window.opener
    : window.parent
    ? window.parent
    : window;

/* MKT-486 — one authentication journey, one login notification.
 *
 * `AUTH_LOGIN_SUCCESS` is raised from four call sites (`successLogin`, `fbLogin`,
 * `socialSuccess`, `AuthTwoFA.done`) and more than one of them fires on the same
 * journey: an MFA-gated password login notifies from `successLogin` AND again when
 * the OTP completes, and `authActions.update` notifies a third time when the user
 * fills the "incomplete profile" form. The HOST turns every message into a GA4
 * `login`, so a repeat is a conversion counted twice.
 *
 * Latch the FIRST notification — it is the one raised by the call site that knows
 * the real `method` — and no-op the rest. Guarding here instead of at each call
 * site keeps the invariant in one place, where the next emitter inherits it.
 */
let _loginNotified = false;

/**
 * Generic Sender
 *
 * @param {string} title   - message title
 * @param {object} content - message content
 * @param {string} type    - message type
 *
 * @return {boolean} operation status
 */
const _generic = (title, content, type) => {
    if (!title || !content) {
        return false;
    }
    const postMessage = _top.postMessage(
        {
            action: PREFIX + title,
            category: type || "INFO",
            content: content,
        },
        "*"
    );
    return postMessage;
};

/**
 * Get Session's User Credentials
 */
const _getCredentials = () => {
    return (
        authService.getCredentials() || {
            userId: "",
            token: "",
            jwt: "",
        }
    );
};

/**
 * Auth User Confirmed
 *
 * @return {boolean} operation status
 */
const authConfirm = () => {
    const { userId, token, jwt } = _getCredentials();
    return _generic(
        "AUTH_CONFIRMED",
        {
            userId: userId,
            token: token,
            authToken: jwt,
        },
        "AUTH"
    );
};

/**
 * Auth User Logout
 *
 * @return {boolean} operation status
 */
const authLogout = () => {
    /* The iframe survives a logout, so the next sign-in is a NEW journey. */
    _loginNotified = false;

    return _generic("AUTH_LOGOUT", {}, "AUTH");
};

/**
 * Login Failure
 *
 * @param {object} reason - API response error
 *
 * @return {boolean} operation status
 */
const authLoginFailure = (reason) => {
    if (!reason) {
        return false;
    }

    return _generic(
        "AUTH_LOGIN_FAIL",
        {
            reason: reason,
        },
        "AUTH"
    );
};

/**
 * Login Success
 *
 * MKT-486 — `method` tells the HOST how the user authenticated, so the host can
 * emit the canonical GA4 `login` event with a faithful `method`. It is the only
 * place that knowledge exists: no other message in this contract carries it, and
 * the host cannot infer it (the mount `path` is falsifiable by in-iframe
 * navigation). Additive field: hosts that ignore it are unaffected.
 *
 * Notifies AT MOST ONCE per journey (see `_loginNotified`): the first call wins,
 * later ones return false without posting. `authLogout` re-arms it.
 *
 * @param {object} userData - optional
 * @param {('email'|'facebook'|'google'|'apple'|'facebank')} [method] - optional
 *
 * @return {boolean} operation status
 */
const authLoginSuccess = (userData = null, method = null) => {
    if (_loginNotified) {
        return false;
    }

    _loginNotified = true;

    const { userId, token, jwt } = _getCredentials();

    return _generic(
        "AUTH_LOGIN_SUCCESS",
        {
            userId: userId,
            token: token,
            authToken: jwt,
            data: userData,
            method: method,
        },
        "AUTH"
    );
};

const setLocale = (locale) => {
    return _generic("AUTH_LOCALE", locale, "AUTH");
};

/**
 * Register Failure
 *
 * @param {object} error - API response error
 *
 * @return {boolean} operation status
 */
const authRegisterFailure = (error) => {
    if (!error) {
        return false;
    }

    return _generic("AUTH_REGISTER_FAIL", error, "AUTH");
};

/**
 * Register Success
 *
 * MKT-486 — see `authLoginSuccess` for why `method` travels with the message.
 *
 * NOTE for hosts: this fires BEFORE any credential exists (registration does not
 * authenticate; the app routes to /login and requires a manual sign-in), so
 * `userId`/`token`/`authToken` are empty strings here. Treat this message as the
 * "this journey is a sign_up" DISCRIMINATOR and read the identity from the later
 * `done` (AUTH_DONE) message, which carries the resolved user.
 *
 * @param {object} userData - optional
 * @param {('email'|'facebook'|'google'|'apple')} [method] - optional
 *
 * @return {boolean} operation status
 */
const authRegisterSuccess = (userData = null, method = null) => {
    const { userId, token, jwt } = _getCredentials();

    return _generic(
        "AUTH_REGISTER_SUCCESS",
        {
            userId: userId,
            token: token,
            authToken: jwt,
            data: userData,
            method: method,
        },
        "AUTH"
    );
};

/**
 * Company Login Success
 *
 * @param {object} companyData
 *
 * @return {boolean} operation status
 */
const authCompanyLoginSuccess = (companyData) => {
    return _generic(
        "AUTH_COMPANY_LOGIN_SUCCESS",
        {
            company: companyData,
        },
        "AUTH"
    );
};

/**
 * Authentication Done
 *
 * @param {object}  companyData - optional
 * @param {boolean} companies   - optional
 * @param {object}  userData    - optional
 */
const authDone = (companyData, companies, userData = null) => {
    const { userId, token, jwt } = _getCredentials();
    const updatedCompanies = [{
        ...companyData,
        authToken: jwt,
        token: token,
    }];

    let _userData = {
        userId: userId,
        token: token,
        authToken: jwt,
        userData: userData,
    };

    let _additionals = {
        origin: window.location.origin,
        path: window.location.pathname,
        search: window.location.search,
    };

    if (updatedCompanies) {
        _additionals.companies = updatedCompanies;
    }

    return _generic(
        "AUTH_DONE",
        Object.assign(
            companyData ? { company: companyData } : _userData,
            _additionals
        )
    );
};

/**
 * Auth Success
 *
 * @param {object} userData
 */
const authSuccess = (userData = null) => {
    if (!userData) {
        return false;
    }
    return _generic("AUTH_DONE", userData);
};

/**
 * Password Recover Sent
 *
 * @param {string} email
 *
 * @return {boolean} operation status
 */
const passwordRecoverSent = (email) => {
    if (!email) {
        return false;
    }

    return _generic("RECOVER_SENT", email, "PASSWORD");
};

/**
 * Password Updated
 *
 * @param {string} email
 *
 * @return {boolean} operation status
 */
const passwordUpdated = (email) => {
    if (!email) {
        return false;
    }

    return _generic("UPDATED", email, "PASSWORD");
};

/**
 * Page Loaded event
 *
 * @return {boolean} operation status
 */
const pageLoaded = () => {
    return _generic("PAGE_LOADED", {
        origin: window.location.origin,
        path: window.location.pathname,
        search: window.location.search,
        width: window.innerWidth,
        height: window.innerHeight,
    });
};

/**
 * Auth User Logout
 *
 * @return {boolean} operation status
 */
const flow = (path = "") => {
    return _generic(
        `AUTH_FLOW`,
        {
            pathname: path.replace("/", ""),
        },
        "FLOW"
    );
};

/**
 * Device trusted
 *
 * @param {boolean} isTrust
 * @param {object}  device
 */
const trusted = (isTrust, device) => {
    return _generic("AUTH_TRUSTED", {
        trusted: isTrust,
        device,
    });
};

/**
 * Redirect application to support
 */
const support = () => {
    return _generic("AUTH_SUPPORT", {
        callSupport: true,
    });
};

const oauthCancelSSO = () => {
    return _generic("AUTH_CANCEL_SSO", {cancel: true}, "AUTH");
};

const oauthCredentialsFailure = () => {
    return _generic("AUTH_CREDENTIALS_FAILURE", {}, "AUTH");
};

/* Constant object to represent Service Functions */
export const postMessageService = {
    authConfirm,
    authLogout,
    authLoginFailure,
    authLoginSuccess,
    authRegisterFailure,
    authRegisterSuccess,
    authCompanyLoginSuccess,
    authDone,
    authSuccess,
    pageLoaded,
    passwordRecoverSent,
    passwordUpdated,
    flow,
    trusted,
    support,
    setLocale,
    oauthCredentialsFailure,
    oauthCancelSSO
};
